import {
  Component,
  ContentChild,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { MultimediaListComponent } from '../../organisms/multimedia-list/multimedia-list.component';
import {
  MultimediaList,
  MultimediaListItem,
  MultimediaIcon,
} from '../../../shared/interfaces/multimedia-list.interface';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { IconComponent } from '../../atoms/icon/icon.component';

import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { Tooltip } from '../../../shared/interfaces/tooltip.interface';

import { CrossReferencing } from '../../../shared/interfaces/cross-referencing.interface';
import {
  FaIconLibrary,
  IconName,
  IconPrefix,
} from '@fortawesome/angular-fontawesome';
import {
  CaasComponent,
  CaasElement,
  CaasElementItem,
  CaasImageValue,
  CaasLinkFormData,
} from '../../../shared/interfaces/caas/caas.interface';
import { ImageComponent } from '../../atoms/image/image.component';
import { ActivatedRoute } from '@angular/router';
import { ImagesService } from '../../../services/images.service';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-material-download',
  standalone: true,
  imports: [
    CommonModule,
    MultimediaListComponent,
    IconComponent,
    TooltipComponent,
    ImageComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './material-download.component.html',
  styleUrls: ['./material-download.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class MaterialDownloadComponent implements OnChanges {
  @ContentChild('slot') projectedContent?: ElementRef;
  @Input() caasData?: CaasComponent;

  @Input() headline: string = '';
  @Input() multimediaLists: MultimediaList[];
  @Input() multimediaImage: string = '';
  @Input() multimediaImageAlt: string = '';
  @Input() multimediaTitle: string = '';
  @Input() headlineItem: {
    label: string;
    leftIcon: string;
    rightIcons: { type: string; href: string; tooltip?: Tooltip }[];
  };
  @Input() crossReferencing?: CrossReferencing;
  @Input() caasCrossReferencing?: CaasComponent;
  baseHref: string = '';
  clipboardTooltip?: Tooltip;

  Sizes = Sizes;
  Colors = Colors;

  tooltip?: Tooltip | null;

  public isLoading = true;

  constructor(
    private readonly library: FaIconLibrary,
    private readonly caasBodytextService: CaasBodytextService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly activatedRoute: ActivatedRoute,
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    this.multimediaLists = [];
    this.headlineItem = { label: '', leftIcon: 'pdf', rightIcons: [] };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      const baseElement = this.document.querySelector('base');
      const angularRoute = this.activatedRoute.routeConfig?.path;
      if (angularRoute && baseElement?.href) {
        this.baseHref = baseElement.href + '' + angularRoute;
      } else {
        this.baseHref = baseElement?.href ?? '';
      }

      this.setCaasContent(changes['caasData'].currentValue);
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  closeTooltip() {
    this.tooltip = null;
  }
  openTooltip(tooltip: Tooltip) {
    this.tooltip = tooltip;
  }

  getIconName(name: string): string {
    if (
      this.library.getIconDefinition('far' as IconPrefix, name as IconName) !==
      null
    ) {
      return name;
    } else {
      return 'user';
    }
  }

  copyToClipboard(url: string, event: TouchEvent | MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(url);
    this.clipboardTooltip = {
      id: this.tooltip?.id ?? '',
      description: '<span>URL kopiert</span>',
    };
    setTimeout(() => {
      this.clipboardTooltip = undefined;
    }, 500);
  }

  setCaasContent(caasData: CaasComponent) {
    safeAssign(
      () => {
        this.headline = caasData?.formData?.st_header?.value ?? '';
      },
      'Assigning headline',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.multimediaLists = this.getCaasMultimediaLists(caasData);
      },
      'Assigning multimedia lists',
      this.errorHandler
    );
    safeAssign(
      () => {
        if (
          caasData?.formData?.st_elements?.value[0]?.template?.uid ===
          'st_download_container'
        ) {
          this.getCaasMultimediaHeadline(
            caasData.formData.st_elements?.value[0]
          );
        }
      },
      'Assigning multimedia headline',
      this.errorHandler
    );
  }

  getCaasMultimediaHeadline(component: CaasElement) {
    const { formData } = component;
    let label = '';
    let title = '';
    let imageValue: CaasImageValue | undefined = undefined;
    let linkValue: CaasLinkFormData | null | undefined = undefined;
    let externalUrl = '';
    safeAssign(
      () => {
        title = formData.st_featured_title?.value ?? '';
      },
      'Assigning title',
      this.errorHandler
    );
    safeAssign(
      () => {
        imageValue = formData.st_featured_image?.value;
      },
      'Assigning image value',
      this.errorHandler
    );
    safeAssign(
      () => {
        linkValue = formData.st_featured_link?.value?.formData;
      },
      'Assigning link value',
      this.errorHandler
    );
    safeAssign(
      () => {
        externalUrl = linkValue?.lt_link_external?.value as string;
      },
      'Assigning external URL',
      this.errorHandler
    );
    const isBrandPortal = externalUrl?.includes(
      'https://www.hdi-brandportal.com'
    );
    this.multimediaTitle = title;
    safeAssign(
      () => {
        if (imageValue?.url) {
          this.multimediaImage = imageValue?.url;
        }
      },
      'Assigning multimedia image',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.multimediaImageAlt = imageValue?.identifier
          ? (this.imagesService.getAltTextById(imageValue.identifier) ?? '')
          : '';
      },
      'Assigning multimedia image alt text',
      this.errorHandler
    );

    safeAssign(
      () => {
        label = linkValue?.lt_link_label?.value ?? '';
      },
      'Assigning label',
      this.errorHandler
    );

    const tooltip = (description: string) => ({
      id: crypto.randomUUID(),
      description: `<span>${description}</span>`,
    });

    const baseIcons = [
      {
        type: 'eye',
        href: externalUrl,
        tooltip: tooltip('Ansicht'),
      },
      ...(isBrandPortal
        ? []
        : [
            {
              type: 'down-to-line',
              href: externalUrl,
              tooltip: tooltip('Download'),
            },
          ]),
      {
        type: 'link',
        href: externalUrl,
        tooltip: tooltip('Link kopieren'),
      },
    ];

    this.headlineItem = {
      label: label,
      leftIcon: 'file-pdf',
      rightIcons: baseIcons,
    };
  }

  getDownloadItem(downloadItem: CaasElementItem): MultimediaListItem | null {
    if (!downloadItem?.formData) return null;

    const tooltip = (text: string) => ({
      id: crypto.randomUUID(),
      description: `<span>${text}</span>`,
    });

    const buildIcons = (href: string, isBrandPortal: boolean) => {
      const icons = [
        {
          type: 'eye',
          href,
          target: '_blank',
          tooltip: tooltip('Ansicht'),
          title: 'Ansicht',
        },
      ];

      if (!isBrandPortal) {
        icons.push({
          type: 'down-to-line',
          href,
          tooltip: tooltip('Download'),
          title: 'Download',
          target: '',
        });
      }

      icons.push({
        type: 'link',
        href,
        tooltip: tooltip('Link kopieren'),
        title: 'Link kopieren',
        target: '',
      });

      return icons;
    };

    const { formData } = downloadItem;
    let label = '';
    let externalUrl = '';
    let rightIcons: MultimediaIcon[] = [];
    safeAssign(
      () => {
        externalUrl = formData.lt_link_external?.value ?? '';
      },
      'Assigning external URL',
      this.errorHandler
    );

    const isBrandPortal = externalUrl.includes(
      'https://www.hdi-brandportal.com'
    );

    safeAssign(
      () => {
        label = formData.lt_link_label?.value ?? '';
      },
      'Assigning label',
      this.errorHandler
    );

    safeAssign(
      () => {
        rightIcons = buildIcons(externalUrl, isBrandPortal);
      },
      'Assigning right icons',
      this.errorHandler
    );

    return {
      label: label,
      link: '',
      leftIcon: 'file-pdf',
      rightIcons: rightIcons,
    };
  }

  getDownloadList(downloads: CaasElement): MultimediaList | null {
    if (!downloads?.formData) return null;

    let title = '';
    let key = '';
    let items: MultimediaListItem[] = [];

    safeAssign(
      () => {
        title = downloads.formData.st_header?.value ?? '';
      },
      'Assigning download title',
      this.errorHandler
    );

    safeAssign(
      () => {
        key = downloads.identifier ?? '';
      },
      'Assigning download key',
      this.errorHandler
    );

    safeAssign(
      () => {
        items =
          downloads.formData.st_elements?.value
            ?.map((downloadItem) => {
              return this.getDownloadItem(downloadItem);
            })
            .filter(
              (item): item is NonNullable<typeof item> => item !== null
            ) ?? [];
      },
      'Assigning download items',
      this.errorHandler
    );

    return {
      title: title,
      key: key,
      backgroundColor: Colors.NEUTRAL_LIGHTER,
      items: items,
    };
  }

  getMultimediaItem(
    multimediaItem: CaasElementItem
  ): MultimediaListItem | null {
    if (!multimediaItem?.formData) return null;
    const tooltip = (text: string) => ({
      id: crypto.randomUUID(),
      description: `<span>${text}</span>`,
    });
    const { formData } = multimediaItem;
    let typeId: string | undefined = undefined;
    let isVideo = false;
    let externalUrl = '';
    let label = '';
    let videoSrc: SafeResourceUrl | undefined = undefined;
    let videoId: string | undefined = undefined;
    let href = externalUrl;
    safeAssign(
      () => {
        typeId = formData?.lt_link_type?.value?.identifier;
      },
      'Assigning multimedia type ID',
      this.errorHandler
    );
    safeAssign(
      () => {
        isVideo = typeId === 'video';
      },
      'Assigning isVideo',
      this.errorHandler
    );
    safeAssign(
      () => {
        externalUrl = formData?.lt_link_external?.value ?? '';
      },
      'Assigning external URL',
      this.errorHandler
    );
    safeAssign(
      () => {
        label = formData?.lt_link_label?.value ?? '';
      },
      'Assigning label',
      this.errorHandler
    );
    safeAssign(
      () => {
        videoSrc = this.getVideoSrc(multimediaItem);
      },
      'Assigning video source',
      this.errorHandler
    );
    safeAssign(
      () => {
        videoId = this.getVideoId(multimediaItem);
      },
      'Assigning video ID',
      this.errorHandler
    );
    safeAssign(
      () => {
        href = isVideo
          ? this.getClipboardVideoSrc(multimediaItem)
          : externalUrl;
      },
      'Assigning href',
      this.errorHandler
    );

    return {
      label: label,
      link: '',
      leftIcon: isVideo ? 'video' : 'image',
      rightIcons: [
        {
          type: isVideo ? 'circle-play' : 'arrow-up-right-from-square',
          href: externalUrl,
          target: '_blank',
          videoSrc: videoSrc,
          thumbnailSrc: '',
          videoId: videoId,
          tooltip: tooltip(isVideo ? 'Abspielen' : 'Öffnen'),
          title: isVideo ? 'Abspielen' : 'Öffnen',
        },
        {
          type: 'link',
          href: href,
          tooltip: tooltip('Link kopieren'),
          title: 'Link kopieren',
        },
      ],
    } as MultimediaListItem;
  }

  getMultimediaList(multimedia: CaasElement): MultimediaList | null {
    if (!multimedia?.formData) return null;
    let title = '';
    let key = '';
    let items: MultimediaListItem[] = [];
    safeAssign(
      () => {
        title = multimedia.formData.st_header?.value ?? '';
      },
      'Assigning multimedia title',
      this.errorHandler
    );
    safeAssign(
      () => {
        key = multimedia.identifier ?? '';
      },
      'Assigning multimedia key',
      this.errorHandler
    );
    safeAssign(
      () => {
        items =
          multimedia.formData.st_elements?.value
            ?.map((multimediaItem) => {
              return this.getMultimediaItem(multimediaItem);
            })
            .filter(
              (item): item is NonNullable<typeof item> => item !== null
            ) ?? [];
      },
      'Assigning multimedia items',
      this.errorHandler
    );
    return {
      title: title,
      key: key,
      items: items,
    };
  }

  getCaasMultimediaLists(component: CaasComponent): MultimediaList[] {
    let downloads: CaasElement | null = null;
    let downloadList: MultimediaList | null = null;
    let multimedia: CaasElement | null = null;
    let multimediaList: MultimediaList | null = null;
    const multimediaLists: MultimediaList[] = [];
    safeAssign(
      () => {
        downloads =
          component?.formData?.st_elements?.value?.find(
            (multimediaItem) =>
              multimediaItem?.template?.uid === 'st_download_container'
          ) ?? null;
      },
      'Assigning downloads',
      this.errorHandler
    );
    safeAssign(
      () => {
        multimedia =
          component?.formData?.st_elements?.value?.find(
            (multimediaItem) =>
              multimediaItem?.template?.uid === 'st_multimedia_container'
          ) ?? null;
      },
      'Assigning multimedia',
      this.errorHandler
    );

    if (downloads) {
      downloadList = this.getDownloadList(downloads);
      if (downloadList) {
        multimediaLists.push(downloadList);
      }
    }
    if (multimedia) {
      multimediaList = this.getMultimediaList(multimedia);
      if (multimediaList) {
        multimediaLists.push(multimediaList);
      }
    }
    return multimediaLists;
  }

  getVideoSrc(elementItem: CaasElementItem) {
    if (
      elementItem?.formData?.lt_link_video_type &&
      elementItem?.formData?.lt_link_vimeo_id &&
      elementItem?.formData?.lt_link_youtube_id
    ) {
      return this.caasBodytextService.getVideoSrc(
        elementItem?.formData?.lt_link_video_type,
        elementItem?.formData?.lt_link_vimeo_id,
        elementItem?.formData?.lt_link_youtube_id
      );
    }
    return '';
  }

  getVideoId(elementItem: CaasElementItem) {
    if (
      elementItem?.formData?.lt_link_video_type &&
      elementItem?.formData?.lt_link_vimeo_id &&
      elementItem?.formData?.lt_link_youtube_id
    ) {
      return this.caasBodytextService.getVideoId(
        elementItem.formData.lt_link_video_type,
        elementItem.formData.lt_link_vimeo_id,
        elementItem.formData.lt_link_youtube_id
      );
    }
    return '';
  }

  getClipboardVideoSrc(elementItem: CaasElementItem) {
    const videoId = this.getVideoId(elementItem);

    return this.baseHref + '/?videoId=' + videoId;
  }
}
